import { useLocation, useNavigate } from 'react-router-dom';
import { FaPhone, FaUser, FaWallet, FaClock, FaPlus } from 'react-icons/fa';
import CustomButton from '../CustomButton';
import moment from 'moment';
import { IoBagHandle } from 'react-icons/io5';
import { RiCalendarScheduleFill } from 'react-icons/ri';
import { FaLocationDot } from 'react-icons/fa6';
import { GrStatusGoodSmall } from 'react-icons/gr';
import { MdNoteAlt } from 'react-icons/md';
import CustomAlert from '../CustomAlert';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPaymentMethod } from '../../../utils/common';

const ConfirmationStep = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const modalRef = useRef<HTMLDivElement>(null);

  const bookingData = location.state || {};

  console.log('bookingData ', bookingData);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, []);

  const handleNavigate = (destination: 'dashboard' | 'home') => {
    if (destination === 'dashboard') {
      navigate('/customer/customer-booking');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center py-4 px-1 sm:px-4">
      <div
        ref={modalRef}
        className="w-full max-w-3xl bg-primary-50/20 border border-primary-100 rounded-2xl shadow-sm p-3 md:p-6 transition-all transform max-h-[95vh] overflow-y-auto"
      >
        {/* Header Row - Checkmark, Title, Description */}
        <div className="flex items-center gap-3 md:gap-4 mb-6">
          <div className="flex items-center justify-center w-10 h-10 md:w-15 md:h-15 flex-shrink-0 border border-green-200 bg-green-100 rounded-full text-green-500 animate-bounce-short">
            <svg
              className="w-6 h-6 md:w-10 md:h-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl md:text-4xl font-black text-primary tracking-tight">
              {t('booking_confirmation.title')}
            </h1>
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-2xl">
              {t('booking_confirmation.subtitle')}
            </p>
          </div>
        </div>

        {/* Structured Booking Details Card - Two Column Layout */}
        <div className="border border-primary-100/80 rounded-xl bg-white overflow-hidden mb-3 shadow-2xs">
          {/* Header ID Sub-strip */}
          <div className="bg-gray-50/70 border-b border-gray-200/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('booking_confirmation.booking_identifier')}
            </span>
            <span className="text-sm font-bold text-primary font-mono">
              {bookingData.bookingId}
            </span>
          </div>

          <div className="p-4 md:p-5">
            {/* Two Column Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
              {/* LEFT COLUMN */}

              {/* Service - Left */}
              <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <IoBagHandle className="text-sm text-primary" />
                  <span className="text-xs font-medium">
                    {t('booking_confirmation.service')}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-body-bold text-gray-800">
                    {bookingData.serviceName}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-yellow-600 justify-end">
                    <GrStatusGoodSmall className="text-[9px] text-yellow-500" />
                    <span>{bookingData.bookingStatus}</span>
                  </div>
                </div>
              </div>

              {/* Staff - Right */}
              <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <FaUser className="text-xs text-primary" />
                  <span className="text-xs font-medium">
                    {t('booking_confirmation.staff_member')}
                  </span>
                </div>
                <span className="text-sm text-body-bold text-gray-800 text-right">
                  {bookingData.staff}
                </span>
              </div>

              {/* Additional Services - Left (Full Width on mobile, Left column on desktop) */}
              {bookingData?.additonalService &&
                bookingData.additonalService.length > 0 && (
                  <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0 col-span-1 md:col-span-1">
                    <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                      <FaPlus className="text-sm text-primary" />
                      <span className="text-xs font-medium">
                        {t('booking_confirmation.additional_services')}
                      </span>
                    </div>
                    <div className="text-right">
                      {bookingData?.additonalService?.map(
                        (
                          service: { name: string; price: number },
                          index: number
                        ) => (
                          <div
                            key={index}
                            className="flex items-center justify-end gap-3"
                          >
                            <p className="text-sm text-body-bold text-gray-800">
                              {service.name}
                            </p>
                            <p className="text-xs font-medium text-gray-500">
                              CAD {service.price.toFixed(2)}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Customer - Right */}
              <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <FaUser className="text-xs text-primary" />
                  <span className="text-xs font-medium">
                    {t('booking_confirmation.customer')}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-body-bold text-gray-800">
                    {bookingData.firstName} {bookingData.lastName}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 justify-end">
                    <FaPhone className="text-[10px]" />
                    <span>{bookingData.phone}</span>
                  </div>
                </div>
              </div>

              {/* Schedule - Left */}
              <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <RiCalendarScheduleFill className="text-sm text-primary" />
                  <span className="text-xs font-medium">
                    {t('booking_confirmation.schedule')}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-body-bold text-gray-800">
                    {moment(bookingData.date).format('LL')}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 justify-end">
                    <FaClock className="text-[10px]" />
                    {bookingData.fromTime} - {bookingData.toTime}
                  </div>
                </div>
              </div>

              {/* Payment - Right */}
              <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <FaWallet className="text-xs text-primary" />
                  <span className="text-xs font-medium">
                    {t('booking_confirmation.payment')}
                  </span>
                </div>
                <span className="text-sm text-body-bold text-gray-800 text-right">
                  {formatPaymentMethod(bookingData.paymentMethod)}
                </span>
              </div>

              {/* Location - Left */}
              <div className="flex items-start justify-between gap-2 pb-2 border-b border-gray-100 md:border-b-0">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <FaLocationDot className="text-xs text-primary" />
                  <span className="text-xs font-medium">
                    {t('booking_confirmation.location')}
                  </span>
                </div>
                <p className="text-sm text-body-bold text-gray-800 text-right max-w-[180px] leading-relaxed">
                  {bookingData.address}, {bookingData.city}, {bookingData.state}{' '}
                  {bookingData.postalCode}
                </p>
              </div>

              {/* Booking Note - Right */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                  <MdNoteAlt className="text-sm text-primary" />
                  <span className="text-xs font-medium whitespace-nowrap">
                    {t('booking_confirmation.booking_note')}
                  </span>
                </div>
                <span className="text-sm text-body-bold text-gray-800 text-right">
                  {bookingData.bookingNotes}
                </span>
              </div>
            </div>

            {/* Total Charged - Full Width */}
            <div className="mt-4 pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
              <span className="text-base font-bold text-primary">
                {t('booking_confirmation.total_charged')}
              </span>
              <span className="text-xl font-black text-primary font-mono">
                CAD {Number(bookingData.total).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Sticky Informational Warning Disclaimer box */}
        <CustomAlert
          hideIcon={true}
          title={t('booking_confirmation.disclaimer_title')}
          description={t('booking_confirmation.disclaimer_desc')}
          color="primary"
        />

        {/* Action Button Segment Controls */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full mt-3">
          <CustomButton
            fullWidth
            label={t('booking_confirmation.go_to_home')}
            variant="flat"
            color="primary"
            radius="md"
            size="md"
            onPress={() => handleNavigate('home')}
            className="w-full sm:w-1/2 font-semibold"
          />
          <CustomButton
            fullWidth
            label={t('booking_confirmation.go_to_dashboard')}
            color="primary"
            radius="md"
            size="md"
            onPress={() => handleNavigate('dashboard')}
            className="w-full sm:w-1/2 font-semibold shadow-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default ConfirmationStep;
